'use client';
/**
 * Grilles d'habilitation — écran du lot N7.
 *
 * ── Ce que cet écran montre, et pourquoi ainsi ─────────────────────────────
 *
 * La grille a cessé d'être un document : chaque compétence est une ligne
 * modifiable, portant son niveau (`routine` obligatoire pour tenir le poste,
 * `expert` supplémentaire) et la MODALITÉ DE VALIDATION héritée du document —
 * OD observation directe · QT questionnaire théorique · MS mise en situation ·
 * VD vérification documentaire. Cette modalité n'est pas un commentaire :
 * c'est la réponse à « sur quoi vous êtes-vous fondé pour habiliter cette
 * personne ? ». Elle est donc affichée sur chaque ligne, pas repliée.
 *
 * DEUX ENTRÉES DE LECTURE (N7-8) — par paillasse et par poste — servies par le
 * même appel : le regroupement est fait au serveur, pour que les deux vues ne
 * puissent pas diverger. Ce qui n'est rattaché à rien est COMPTÉ et dit : une
 * grille sans paillasse ni poste n'atteindrait jamais personne, et le silence
 * la rendrait invisible.
 *
 * ── Ce que l'écran refuse de laisser croire ────────────────────────────────
 *
 * Cocher n'habilite pas. L'écran ne montre jamais « habilité » à partir de
 * lignes cochées : il montre un avancement, les avis manquants nommément, et
 * la décision seulement quand elle a été prononcée.
 */
import React from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { useRemonteEnHaut, I, Modale, TitrePage, card, inputStyle, EmptyState, RefISO, Skeleton, Selecteur, SelecteurPastilles, Pastille } from '@/components/ui';
import { Tableau } from '@/shared/Tableau';
import { AccesRefuseRh } from '@/components/screens/RhAbsences';
import { SuppressionRaisonnee, type Consequence } from '@/components/SuppressionRaisonnee';
import { nomDe } from '@/lib/nom';
import { MODALITES, METHODES, lireModalites, ecrireModalites } from '@/lib/modalites-validation';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { styleBouton } from '@/shared/boutonStyles';
export type NiveauTache = 'routine' | 'avance' | 'expert';
export const LIB_NIVEAU: Record<NiveauTache, string> = {
  routine: 'Routine', avance: 'Avancé', expert: 'Expert',
};
/** UNE TÂCHE. Depuis le 25/08/2026 elle porte SES rattachements — poste (avec
 *  le niveau exigé pour chacun), disciplines, processus, équipement — au lieu
 *  de les hériter de la grille qui l'héberge. Les grilles sont devenues des
 *  vues ; ce sont les tâches qui disent à quoi elles se rapportent. */
export type Ligne = {
  id: string; code: string; section: string; libelle: string; methode: string;
  niveau: NiveauTache; ordre: number; actif: boolean;
  postes?: { code: string; niveau?: NiveauTache }[];
  disciplines?: string[];
  processus?: string[];
  equipementCode?: string | null;
  /** Les habilitations dont la tâche fait partie — PLUSIEURS (lot B, 27/08/2026).
   *  Le singulier `habilitationCode` reste servi à côté, rempli de la première :
   *  le retirer d'un coup viderait en silence les écrans qui le lisent. */
  habilitationCodes?: string[];
  habilitationCode?: string | null;
  /** Mises en situation (documentIds) qui évaluent la ligne — lot 4 du parcours (18/10/2026). */
  situations?: string[];
};
export type Nature = 'paillasse' | 'sous-paillasse' | 'transverse' | 'poste';
export type Grille = {
  id: string; code: string; libelle: string; lettre: string; chapo: string;
  discipline: string | null; nature: Nature; paillasses: string[];
  posteCode: string | null; sousTraitance: boolean;
  postes: string[]; fonction: string; cycle: number;
  origine: string; versionSource: string; version: number; actif: boolean;
  /** Codes des documents de référence (lot 3 du parcours, 18/10/2026). */
  documents?: string[];
  nbLignes: number; nbRoutine: number; nbAvance?: number; nbExpert: number; lignes: Ligne[];
};
export type LigneComposee = {
  code: string; libelle: string; grilleCode: string;
  niveau: NiveauTache; niveauOrigine: NiveauTache;
};
export type Composition = {
  code: string; libelle: string; lignes: LigneComposee[];
  introuvables: string[]; nbRoutine: number; nbExpert: number;
};
export type Registre = {
  versionSource: string; grilles: Grille[];
  parPaillasse: { code: string; libelle: string; grilles: string[] }[];
  parPoste: { code: string; libelle: string; grilles: string[] }[];
  parNature: { nature: Nature; grilles: string[] }[];
  perimetres: { code: string; jointes: string[]; nbLignesJointes: number }[];
  compositions: Composition[];
  rapprochement: {
    communs: string[]; disciplinesSansFamille: string[]; famillesSansDiscipline: string[];
    paillassesSansDiscipline: string[]; nbDisciplines: number; nbPaillasses: number;
    nbFamilles: number; avertissement: string;
  };
  sansPaillasse: string[]; sansParent: string[];
  parentsIgnores: { code: string; ignores: string[] }[];
  sansPoste: string[];
};

/**
 * LES TROIS NATURES, dites à l'écran — parce que les confondre est l'erreur que
 * ce lot corrige, et qu'un badge muet la laisserait se reformer.
 */
export const NATURES: Record<Nature, { court: string; long: string; fond: string; trait: string }> = {
  paillasse: {
    court: 'paillasse', fond: 'var(--gray-100)', trait: 'var(--text-muted)',
    long: 'Un lieu de travail — une discipline. Seule nature qui porte une case dans la matrice des habilitations.',
  },
  'sous-paillasse': {
    court: 'sous-paillasse', fond: 'var(--blue-50)', trait: 'var(--blue-700)',
    long: 'Une activité qui TRAVERSE plusieurs paillasses. Ses lignes apparaissent dans le dossier de chacune sans y être recopiées — elles n’existent qu’une fois.',
  },
  transverse: {
    court: 'bloc transverse', fond: 'var(--violet-50)', trait: 'var(--violet-600)',
    long: 'Un bloc présent dans TOUT dossier, quelle que soit la paillasse. Ses lignes ne sont écrites qu’une fois : les corriger ici les corrige partout.',
  },
  poste: {
    court: 'poste', fond: 'var(--amber-50)', trait: 'var(--amber-600)',
    long: 'Des lignes propres à un POSTE et non à un lieu. Elles n’entrent dans aucun dossier de paillasse : elles composent un poste, depuis l’onglet « Composition des postes ».',
  },
};

export function BadgeNature({ n }: { n: Nature }) {
  const d = NATURES[n] || NATURES.paillasse;
  /* L9 : sur la Pastille du socle (É-12) ; la couleur dit la NATURE, un sens métier. */
  return <Pastille bg={d.fond} fg={d.trait} compacte title={d.long}>{d.court}</Pastille>;
}

/** Les quatre codes du document, écrits en toutes lettres au survol. */
/* ⚠️ LE VOCABULAIRE A DÉMÉNAGÉ dans `lib/modalites-validation.ts` (12/09/2026),
   avec la lecture et l'écriture de la combinaison. Il vivait ici, dans un
   `.tsx` — donc hors de portée du lanceur de tests, qui n'éprouve que les
   modules purs. Or ce qui compte n'est pas la table des libellés mais l'ORDRE
   d'écriture : c'est lui qui ferme la dérive « OD + QT » / « QT + OD »
   (six lignes mal rangées sur 399, mesurées en base). Réexporté : l'écran s'en
   sert encore pour l'infobulle. */
export { METHODES } from '@/lib/modalites-validation';

export function Methode({ code }: { code: string }) {
  if (!code) {
    // Le document lui-même laisse 38 cellules « Méthode » vides. On le DIT :
    // une case vide qu'on habille d'une valeur par défaut mentirait sur ce que
    // le laboratoire a réellement écrit.
    return (
      <span title="La grille d’origine ne précise pas comment cette ligne se valide — à compléter par le laboratoire"
        style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600 }}>modalité à définir</span>
    );
  }
  const detail = lireModalites(code).map((c) => METHODES[c] || c).join(' · ');
  return (
    <span title={detail} style={{
      fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', border: '1px solid var(--border-default)',
      borderRadius: 'var(--radius-sm)', padding: '1px 6px', whiteSpace: 'nowrap',
    }}>{code}</span>
  );
}

/* ⚠️ `decouper` A ÉTÉ RETIRÉ le 12/09/2026. Il découpait « BIOCH, HEMATO » en
   codes pour les deux champs à virgules — disciplines et processus —, qui sont
   désormais des listes. Le rendre à une liste VIDE reste possible et voulu (on
   retire un rattachement en retirant ses pastilles) : c'est `SelecteurPastilles`
   qui s'en charge, avec un tableau vide, exactement comme avant.
   Gardé mort, il aurait fait croire qu'un champ libre subsiste quelque part. */

/**
 * Les postes qui exigent une tâche, avec LE NIVEAU DE CHACUN.
 *
 * Le niveau est par poste et non global : une maintenance « expert » en journée
 * devient obligatoire en garde isolée (arbitrage du 07/08/2026). Un seul niveau
 * pour tous les postes obligerait à dédoubler la tâche.
 */
function ChampPostes({ valeur, onChange, niveauParDefaut }: {
  valeur: { code: string; niveau?: NiveauTache }[];
  onChange: (v: { code: string; niveau?: NiveauTache }[]) => void;
  niveauParDefaut: NiveauTache;
}) {
  const { rhPostes } = useGed();
  const [ajout, setAjout] = React.useState('');
  const dejaLa = new Set(valeur.map((p) => p.code));
  const libelle = (code: string) => rhPostes.find((p) => p.code === code)?.libelle || code;

  return (
    <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
      {!valeur.length && (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Aucun poste déclaré — la tâche suit celui de sa grille.
        </span>
      )}
      {valeur.map((p, i) => (
        <div key={p.code} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, minWidth: 90 }}>{p.code}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1, minWidth: 0 }}>{libelle(p.code)}</span>
          <select style={{ ...inputStyle, width: 130, height: 30, fontSize: 12.5 }}
            value={p.niveau || niveauParDefaut}
            onChange={(e) => {
              const l = valeur.slice();
              l[i] = { ...p, niveau: e.target.value as NiveauTache };
              onChange(l);
            }}>
            {(['routine', 'avance', 'expert'] as NiveauTache[]).map((n) => (
              <option key={n} value={n}>{LIB_NIVEAU[n]}</option>
            ))}
          </select>
          <Bouton taille="sm" type="button" title={`Retirer ${p.code}`} onClick={() => onChange(valeur.filter((x) => x.code !== p.code))}>
            <I n="x" s={12} />
          </Bouton>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8 }}>
        <select style={{ ...inputStyle, height: 32, fontSize: 12.5, flex: 1 }} value={ajout}
          onChange={(e) => setAjout(e.target.value)}>
          <option value="">Ajouter un poste…</option>
          {rhPostes.filter((p) => !dejaLa.has(p.code)).map((p) => (
            <option key={p.code} value={p.code}>{p.code} — {p.libelle}</option>
          ))}
        </select>
        <Bouton variante="primaire" taille="sm" type="button" disabled={!ajout} onClick={() => { onChange([...valeur, { code: ajout, niveau: niveauParDefaut }]); setAjout(''); }}>
          Ajouter
        </Bouton>
      </div>
    </div>
  );
}

export function Niveau({ n }: { n: NiveauTache }) {
  const exp = n === 'expert';
  return (
    <span
      title={exp
        ? 'Supplémentaire : maintenance lourde, archivage, dépannage. N’entre PAS dans le prononcé « habilité au poste ».'
        : 'Obligatoire pour tenir le poste. Toutes les lignes de routine doivent être acquises.'}
      style={{
        fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '1px 7px',
        background: exp ? 'var(--violet-50)' : 'var(--gray-100)', color: exp ? 'var(--violet-600)' : 'var(--text-muted)',
      }}
    >{exp ? 'expert' : 'routine'}</span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * RETRAIT D'UNE LIGNE DE COMPÉTENCE (GED-16)
 *
 * `DELETE /ged/rh/grilles/ligne/:id` était autorisée depuis sa livraison sans
 * qu'aucun écran ne l'atteigne. Elle N'EFFACE RIEN : le moteur fait
 * `update({ actif: false })`, journalise `LigneGrilleRetiree` et répond
 * `{ retiree: true, supprimee: false }` — le commentaire du contrôleur dit
 * pourquoi : « des dossiers déjà renseignés portent son résultat, et effacer la
 * ligne rendrait ces enregistrements illisibles — acquis sur quoi ? ».
 *
 * ⚠️ C'est ce qui décide du sort de cette route : rien d'opposable n'est
 * touché. Une décision d'habilitation prononcée reste prononcée, l'état écrit
 * par le tuteur reste lisible, et la ligne se remet en service. Ce geste ne
 * demande donc PAS un refus — il demande d'être vu. Ce qu'il change vraiment,
 * c'est le PÉRIMÈTRE des dossiers en cours : la ligne sort du dénominateur, et
 * un dossier peut devenir prononçable du seul fait qu'on a retiré ce qui
 * manquait. C'est cela qu'il faut montrer avant le clic, pas un « êtes-vous
 * sûr ? ».
 * ═════════════════════════════════════════════════════════════════════════ */

type DossierUsage = {
  id: string; personne: string; grilleCode: string; grillesJointes: string[];
  etats: Record<string, unknown> | null; decision: string | null; statut: string;
};

/** Les dossiers qui portent un résultat sur cette ligne, et ceux dont elle
 *  change le périmètre. Calculé sur les données servies par
 *  `GET ged/rh/grilles/dossiers` — jamais deviné. */
function consequencesLigne(l: Ligne, g: Grille, dossiers: DossierUsage[]): Consequence[] {
  const porte = (d: DossierUsage) => !!d.etats && Object.prototype.hasOwnProperty.call(d.etats, l.code);
  const usages = dossiers.filter(porte);
  const prononces = usages.filter((d) => !!d.decision);
  const auPerimetre = dossiers.filter((d) => !d.decision
    && (d.grilleCode === g.code || (d.grillesJointes || []).includes(g.code)));
  const noms = (xs: DossierUsage[]) => xs.slice(0, 6).map((d) => d.personne || d.id).join(' · ')
    + (xs.length > 6 ? ` … (+${xs.length - 6})` : '');

  const c: Consequence[] = [];
  if (usages.length) {
    c.push({
      cle: 'resultats', gravite: 'attention',
      texte: `${usages.length} dossier(s) de grille portent un résultat évalué sur cette ligne : ${noms(usages)}.`,
      detail: 'Ces résultats ne sont pas effacés — la ligne est désactivée, pas supprimée. Ils restent lisibles dans le dossier, avec la méthode employée, la date et le visa.',
    });
  }
  if (prononces.length) {
    c.push({
      cle: 'prononces', gravite: 'attention',
      texte: `Dont ${prononces.length} où une décision d’habilitation a déjà été prononcée : ${noms(prononces)}.`,
      detail: 'La décision n’est pas remise en cause et ne peut pas l’être ici : elle a été prise sur la version de grille que le dossier a figée. Retirer la ligne ne réécrit pas ce qui a été signé.',
    });
  }
  if (auPerimetre.length) {
    c.push({
      cle: 'perimetre', gravite: l.niveau === 'routine' ? 'attention' : 'info',
      texte: `${auPerimetre.length} dossier(s) en cours perdent cette ligne de leur périmètre.`,
      detail: l.niveau === 'routine'
        ? 'C’est une ligne de ROUTINE : elle était obligatoire pour tenir le poste. Un dossier bloqué par elle seule deviendra prononçable du seul fait de ce retrait — c’est l’effet à peser.'
        : 'C’est une ligne EXPERT : elle n’entre pas dans le prononcé « habilité au poste ». L’avancement affiché change, la condition de prononcé non.',
    });
  }
  c.push({
    cle: 'version', gravite: 'info',
    texte: `La version de la grille ne change pas (elle reste v${g.version}).`,
    detail: 'Le moteur n’incrémente la version qu’à la création et à la modification d’une ligne, pas à son retrait. Le cartouche de la grille imprimée restera donc identique bien qu’elle perde une compétence.',
  });
  c.push({
    cle: 'trace', gravite: 'info',
    texte: 'Le retrait est inscrit au journal chaîné (LigneGrilleRetiree) et reste réversible.',
    detail: 'La ligne réapparaît barrée dans cette grille, avec un bouton « Remettre en service ».',
  });
  return c;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * CE QUE LES GRILLES ONT APPORTÉ À LA MATRICE (GED-15 c)
 *
 * `GET /ged/rh/grilles/matrice` était autorisée depuis sa livraison sans qu'aucun
 * écran ne l'atteigne. Elle répond à la seule question que cet écran-ci pose
 * sans y répondre : « ma décision d'habilitation atteint-elle vraiment la
 * matrice qui décide des affectations ? »
 *
 * La matrice n'est PAS écrite quand on prononce une décision — elle est
 * recalculée à la lecture, en fusionnant les 205 entrées historiques (saisies
 * pour l'encadrement, indexées par NOM de discipline) avec ce que les dossiers
 * de grille ont prononcé (indexés par CODE, traduits au vol). Rien à l'écran ne
 * montrait cette fusion : on prononçait, et on croyait.
 *
 * ⚠️ La ligne qui compte est `perdues`. La fusion ne fait que MONTER une case,
 * jamais descendre — c'est une garantie tenue et testée (`rh-grilles.ts`), et
 * son sens est que le jour de la bascule, où aucune grille n'était encore
 * déroulée, les 205 entrées ne devaient pas disparaître et rendre tout le monde
 * affectable partout. `perdues` doit donc TOUJOURS valoir zéro : autre chose que
 * zéro est une régression du moteur, pas une donnée à interpréter. L'écran le
 * dit en ces termes plutôt que d'afficher un compteur de plus.
 *
 * ⚠️ Chargée AU CLIC : elle relit tous les dossiers de grille, paginés.
 * ═════════════════════════════════════════════════════════════════════════ */

type Matrice = Record<string, Record<string, string>>;
type EtatMatrice = {
  stockee: Matrice; vue: Matrice;
  diff: { casesAvant: number; casesApres: number; ajoutees: string[]; montees: string[]; perdues: string[] };
};

/** « ORG-07::Biochimie » se relit mal. On rend la personne et la discipline. */
function litCle(cle: string, nomDe: (orgId: string) => string) {
  const [org, disc] = cle.split('::');
  return { qui: nomDe(org) || org, discipline: disc || '—' };
}

function ApportDesGrilles({ nomDe }: { nomDe: (orgId: string) => string }) {
  const [ouvert, setOuvert] = React.useState(false);
  const [etat, setEtat] = React.useState<EtatMatrice | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [lecture, setLecture] = React.useState(false);

  const basculer = () => {
    const versOuvert = !ouvert;
    setOuvert(versOuvert);
    if (!versOuvert || etat || lecture) return;
    setLecture(true);
    void fetch('/api/ged/ged/rh/grilles/matrice', { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        // Un échec est DIT. Un « 0 case ajoutée » silencieux se lirait « les
        // grilles n'apportent rien », ce qui est exactement le contraire de ce
        // qu'une lecture ratée établit.
        if (j) { setEtat(j); setErreur(''); } else setErreur('La matrice n’a pas pu être lue.');
      })
      .catch(() => setErreur('La matrice n’a pas pu être lue.'))
      .finally(() => setLecture(false));
  };

  const d = etat?.diff;
  return (
    <div style={{ ...card, padding: 0, marginTop: 12, overflow: 'hidden' }}>
      <button onClick={basculer} aria-expanded={ouvert}
        style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
          cursor: 'pointer', padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
        <I n={ouvert ? 'chevron-bas' : 'chevron-right'} s={14} />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Ce que les grilles ont apporté à la matrice d’habilitations
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          prononcer une décision ne l’écrit pas — elle se recalcule à la lecture
        </span>
        <span style={{ flex: 1 }} />
        {lecture && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>lecture…</span>}
      </button>

      {ouvert && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border-default)' }}>
          {erreur && (
            <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--red-600)', fontWeight: 700 }}>
              {erreur} Ce n’est pas « les grilles n’apportent rien » : c’est une lecture qui a échoué.
            </p>
          )}
          {!erreur && !d && !lecture && (
            <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture de la matrice…</p>
          )}
          {d && (
            <>
              <p style={{ margin: '13px 0 0', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.6, maxWidth: '86ch' }}>
                <strong>{d.casesAvant}</strong> case(s) saisies pour l’encadrement, <strong>{d.casesApres}</strong>{' '}
                effectivement lues par le moteur d’affectation. L’écart vient des décisions prononcées
                sur les dossiers de grille : <strong>{d.ajoutees.length}</strong> case(s) ajoutée(s) et{' '}
                <strong>{d.montees.length}</strong> montée(s) d’un niveau. La matrice n’est jamais écrite —
                elle est recomposée à chaque lecture, ce qui fait qu’une décision prise ce matin
                s’applique aux affectations de cet après-midi.
              </p>

              <p style={{
                margin: '12px 0 0', fontSize: 12.5, lineHeight: 1.6, maxWidth: '86ch',
                borderRadius: 'var(--radius-md)', padding: '9px 13px',
                background: d.perdues.length ? 'var(--red-50)' : 'var(--green-50)',
                color: d.perdues.length ? 'var(--red-600)' : 'var(--green-600)',
              }}>
                {d.perdues.length ? (
                  <><strong>{d.perdues.length} case(s) PERDUE(S).</strong> La fusion ne doit jamais faire
                    descendre une case — c’est ce qui garantit qu’une personne habilitée de longue date
                    ne redevient pas non habilitée le jour où sa grille est déroulée. Une case perdue
                    n’est pas une donnée à interpréter, c’est une régression à signaler :{' '}
                    {d.perdues.slice(0, 8).map((k) => {
                      const l = litCle(k, nomDe);
                      return `${l.qui} · ${l.discipline}`;
                    }).join(' — ')}{d.perdues.length > 8 ? ' …' : ''}</>
                ) : (
                  <><strong>Aucune case perdue.</strong> La fusion n’a fait que monter des cases, jamais
                    en retirer : les entrées saisies pour l’encadrement sont toutes encore lues.</>
                )}
              </p>

              {[
                ['Cases ajoutées par une décision de grille', d.ajoutees] as const,
                ['Cases montées d’un niveau', d.montees] as const,
              ].map(([titre, liste]) => (liste.length ? (
                <div key={titre} style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 6 }}>
                    {titre} ({liste.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {liste.map((k) => {
                      const l = litCle(k, nomDe);
                      return (
                        <span key={k} style={{ fontSize: 12.5, background: 'var(--gray-25)',
                          borderRadius: 'var(--radius-sm)', padding: '3px 9px', color: 'var(--text-body)' }}>
                          {l.qui} <span style={{ color: 'var(--text-muted)' }}>· {l.discipline}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              ) : null))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * LA VUE PAR AXE — ce que la refonte du 25/08/2026 rend possible.
 *
 * On ne demande plus « quelles lignes contient cette grille ? » mais « quelles
 * tâches se rapportent à ce poste / cet appareil / cette discipline / ce
 * processus ? ». Une même tâche répond à plusieurs questions sans être
 * dupliquée : elle n'a qu'un enregistrement, donc une seule vérité.
 *
 * L'écran DIT ce qui est hérité de la grille et ce qui est déclaré sur la
 * tâche : sans cette distinction, on croirait avoir rattaché ce qu'on n'a fait
 * que subir — et on ne saurait pas quoi corriger.
 */
function VueParAxe() {
  const { rhPostes, equips, settings } = useGed();
  const [axe, setAxe] = React.useState<'poste' | 'equipement' | 'discipline' | 'processus'>('poste');
  const [valeur, setValeur] = React.useState('');
  /* L'état se DÉRIVE de ce qu'on a demandé et de ce qu'on a reçu, au lieu
     d'être posé dans l'effet — `setState` synchrone dans un effet déclenche des
     rendus en cascade, et c'est une erreur de lint dans ce dépôt. `pour` note
     la valeur à laquelle la réponse correspond : sans lui, une réponse lente
     s'afficherait sous une autre sélection. */
  const [res, setRes] = React.useState<{ pour: string; taches?: TacheVue[] } | null>(null);
  const [echecs, setEchecs] = React.useState<string[]>([]);

  type TacheVue = {
    code: string; libelle: string; section: string; niveau: string; niveauLibelle: string;
    postes: { code: string; niveau?: string }[]; disciplines: string[]; processus: string[];
    equipementCode: string | null;
    herite: { postes: boolean; disciplines: boolean; processus: boolean };
    grille: { code: string; libelle: string } | null;
  };

  const choix = axe === 'poste' ? rhPostes.map((p) => ({ v: p.code, l: `${p.code} — ${p.libelle}` }))
    : axe === 'equipement' ? (equips || []).filter((e) => e.code).map((e) => ({ v: String(e.code), l: `${e.code} — ${e.nom}` }))
      : axe === 'discipline' ? (settings?.disciplines || []).filter((d) => d.active).map((d) => ({ v: String(d.code || d.name), l: d.name || String(d.code) }))
        : ((settings?.processus || []) as { id: string; name: string }[]).map((p) => ({ v: p.id, l: p.name || p.id }));

  const cle = `${axe}|${valeur}`;
  React.useEffect(() => {
    if (!valeur) return undefined;
    let vivant = true;
    void fetch(`/api/ged/ged/rh/taches?axe=${axe}&valeur=${encodeURIComponent(valeur)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!vivant) return; if (j) setRes({ ...j, pour: cle }); else setEchecs((l) => [...l, cle]); })
      .catch(() => { if (vivant) setEchecs((l) => [...l, cle]); });
    return () => { vivant = false; };
  }, [axe, valeur, cle]);

  const etat: 'vide' | 'charge' | 'pret' | 'echec' = !valeur ? 'vide'
    : echecs.includes(cle) ? 'echec'
      : res?.pour === cle ? 'pret' : 'charge';
  const taches = (etat === 'pret' ? res?.taches || [] : []) as TacheVue[];

  return (
    <div style={{ ...card, padding: 18, marginTop: 12, display: 'grid', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
        Les tâches ne sont plus <em>rangées</em> dans une grille : elles déclarent à quoi elles se
        rapportent, et apparaissent dans toutes les vues concernées. Une tâche vue ici et ailleurs
        est <strong>la même</strong> — il n’y en a qu’un exemplaire.
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12.5 }}>Axe
          <select style={{ ...inputStyle, width: 170 }} value={axe}
            onChange={(e) => { setAxe(e.target.value as typeof axe); setValeur(''); }}>
            <option value="poste">Poste</option>
            <option value="equipement">Équipement</option>
            <option value="discipline">Discipline</option>
            <option value="processus">Processus</option>
          </select>
        </label>
        <label style={{ fontSize: 12.5, flex: '1 1 260px' }}>Valeur
          <select style={{ ...inputStyle, width: '100%' }} value={valeur} onChange={(e) => setValeur(e.target.value)}>
            <option value="">Choisir…</option>
            {choix.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </label>
      </div>

      {etat === 'vide' && <EmptyState titre="Choisissez une valeur pour voir les tâches qui s’y rapportent." />}
      {etat === 'charge' && <Skeleton rows={3} />}
      {etat === 'echec' && <EmptyState titre="La vue n’a pas pu être lue." indice="Réessayez ; si cela persiste, prévenez le Responsable Qualité." />}
      {etat === 'pret' && !taches.length && (
        <EmptyState titre="Aucune tâche ne se rapporte à cette valeur."
          indice="Ce n’est pas une anomalie : c’est un rattachement qui reste à déclarer, sur la tâche elle-même." />
      )}

      {etat === 'pret' && !!taches.length && (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          {taches.map((t, i) => (
            <div key={t.code} style={{ padding: '10px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', display: 'grid', gap: 4 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Niveau n={t.niveau as NiveauTache} />
                <span style={{ fontSize: 13, color: 'var(--text-strong)', flex: '1 1 240px' }}>{t.libelle}</span>
                {t.grille && (
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }} title="Grille qui héberge la tâche — elle ne détermine plus à quoi la tâche se rapporte.">
                    {t.grille.code}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
                {/* HÉRITÉ ou DÉCLARÉ : la distinction est ce qui permet de savoir
                    quoi corriger. Un rattachement hérité n'a pas été choisi. */}
                <Axe libelle="postes" valeurs={t.postes.map((p) => `${p.code}${p.niveau && p.niveau !== t.niveau ? ` (${p.niveau})` : ''}`)} herite={t.herite.postes} />
                <Axe libelle="disciplines" valeurs={t.disciplines} herite={t.herite.disciplines} />
                <Axe libelle="processus" valeurs={t.processus} herite={t.herite.processus} />
                {t.equipementCode && <Axe libelle="équipement" valeurs={[t.equipementCode]} herite={false} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Un axe de rattachement, et d'où il vient. Rien n'est affiché quand l'axe est
 *  vide : une étiquette « aucun » sur chaque ligne ferait un mur où l'on ne
 *  verrait plus les rattachements réels. */
function Axe({ libelle, valeurs, herite }: { libelle: string; valeurs: string[]; herite: boolean }) {
  if (!valeurs.length) return null;
  return (
    <span title={herite ? `Hérité de la grille — non déclaré sur la tâche` : 'Déclaré sur la tâche'}
      style={{
        borderRadius: 'var(--radius-pill)', padding: '2px 9px',
        background: herite ? 'var(--gray-25)' : 'var(--cyan-50)',
        color: herite ? 'var(--text-muted)' : 'var(--cyan-700)',
        border: herite ? '1px dashed var(--border-default)' : '1px solid var(--cyan-200)',
      }}>
      {libelle} : {valeurs.join(', ')}{herite ? ' · hérité' : ''}
    </span>
  );
}

/**
 * LA SECTION D'UNE TÂCHE — choisir parmi l'existant, ou en créer une.
 *
 * ── Pourquoi (RQ, 11/09/2026) ──────────────────────────────────────────────
 *
 * « La section doit être une liste déroulante également ; prévoir de pouvoir
 *  rajouter une nouvelle section. »
 *
 * Le champ était libre, et le registre en a fait **78 sections distinctes pour
 * 399 lignes** — mesuré en base. C'est le mécanisme des 54 graphies de salle :
 * un vocabulaire de fait, jamais déclaré, que chacun retape.
 *
 * ⚠️ LA CRÉATION EST UN GESTE SÉPARÉ, ET C'EST TOUT LE POINT. Une option
 * « autre… » dans la liste se remplit dès le premier oubli, et l'on retrouve
 * les graphies en six mois — c'est l'argument qui a fait refuser l'échappatoire
 * sur les salles du laboratoire. Ici, « Nouvelle section… » bascule la liste en
 * champ de saisie : on sait qu'on en crée une.
 *
 * ⚠️ AUCUNE NORMALISATION À LA VOLÉE. On ne rapproche pas « Prélèvement » de
 * « Prélèvements » : ce serait décider à la place du laboratoire lesquelles des
 * 78 sont la même. La liste arrête la dérive, elle ne répare pas le passé.
 */
function ChampSection({ valeur, sections, onChange }: {
  valeur: string; sections: string[]; onChange: (v: string) => void;
}) {
  /* Une valeur hors liste est forcément une création en cours (ou une section
     supprimée depuis) : on ouvre directement en saisie, sinon le `Selecteur`
     l'écraserait par sa première option. */
  const [creation, setCreation] = React.useState(() => !!valeur && !sections.includes(valeur));
  if (creation) {
    return (
      <label style={{ fontSize: 12.5 }}>Nouvelle section
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={inputStyle} value={valeur} autoFocus
            placeholder="ex. Vérifications périodiques"
            onChange={(e) => onChange(e.target.value)} />
          <Bouton taille="sm" type="button" onClick={() => { onChange(''); setCreation(false); }}>Choisir dans la liste</Bouton>
        </div>
      </label>
    );
  }
  return (
    <label style={{ fontSize: 12.5 }}>Section
      <div style={{ display: 'flex', gap: 8 }}>
        <Selecteur value={valeur} style={inputStyle} onChange={onChange}>
          <option value="">— aucune —</option>
          {sections.map((x) => <option key={x} value={x}>{x}</option>)}
        </Selecteur>
        <Bouton taille="sm" type="button" onClick={() => { onChange(''); setCreation(true); }}>Nouvelle section…</Bouton>
      </div>
    </label>
  );
}

export function RhGrillesEcran() {
  const { rhAcces, session, rhPersonnel, equips, settings, docs } = useGed();
  const [reg, setReg] = React.useState<Registre | null>(null);
  const [erreur, setErreur] = React.useState('');
  /* ⚠️ LE POSTE EST L'ENTRÉE PAR DÉFAUT DEPUIS LE 12/09/2026. « Au lieu de par
     paillasse m'afficher les habilitations par poste. Oublier les familles
     biologiques ainsi. » — RQ, 11/09. Les trois entrées restent, et c'est
     voulu : la paillasse sert encore à rapprocher les disciplines du catalogue.
     Mais c'est le poste qu'on tient, et c'est par lui qu'on arrive. */
  const [entree, setEntree] = React.useState<'paillasse' | 'poste' | 'nature' | 'axe'>('poste');
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(entree);
  const [requalif, setRequalif] = React.useState<string>('');
  const [ouverte, setOuverte] = React.useState<string>('');
  const [edite, setEdite] = React.useState<(Partial<Ligne> & { grilleCode: string }) | null>(null);
  // Lot 3 (11/08/2026) — la création d'une grille manquait à l'écran alors que
  // la route existait (`POST ged/rh/grilles`, RQ).
  const [nouvelleGrille, setNouvelleGrille] = React.useState<{ code: string; libelle: string; nature: string; discipline: string } | null>(null);
  const [enCours, setEnCours] = React.useState(false);
  // Le retrait d'une ligne : la ligne visée, sa grille, et les dossiers lus AU
  // CLIC. Ils ne sont pas chargés au montage — 1 255 journées et autant de
  // dossiers pour un écran que neuf consultations sur dix ouvrent pour lire une
  // grille, pas pour retirer une ligne.
  const [retrait, setRetrait] = React.useState<{ ligne: Ligne; grille: Grille } | null>(null);
  const [dossiers, setDossiers] = React.useState<DossierUsage[] | null>(null);
  const [lectureUsages, setLectureUsages] = React.useState(false);

  // Le rechargement passe par un COMPTEUR et non par un rappel : appeler
  // setState au corps d'un effet est une erreur de lint dans ce dépôt
  // (`react-hooks/set-state-in-effect`), et le garde `vivant` évite d'écrire
  // dans un composant démonté — même modèle que l'écran Continuité.
  const [tour, setTour] = React.useState(0);
  /* Lot 4 du parcours : la banque des mises en situation, pour relier une
     ligne à ce qui l'évalue. Lecture seule ici ; elle se gère dans son écran. */
  const [situations, setSituations] = React.useState<{ documentId: string; titre: string; discipline: string; statut: string }[]>([]);
  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/situations', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant && j && Array.isArray(j.situations)) setSituations(j.situations); })
      .catch(() => { /* la banque est un confort de l'éditeur : son absence ne bloque pas la grille */ });
    return () => { vivant = false; };
  }, []);
  const recharger = () => setTour((t) => t + 1);
  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/grilles', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!vivant) return; if (j) { setReg(j); setErreur(''); } else setErreur('Le registre des grilles n’a pas pu être lu.'); })
      .catch(() => { if (vivant) setErreur('Le registre des grilles n’a pas pu être lu.'); });
    return () => { vivant = false; };
  }, [tour]);

  /* Habilitations & formation : RQ et responsable technique, le RH en lecture
     (05/10/2026) — on suit la rubrique `rhhab`, plus le seul rôle `rh`. */
  if (!(useGed.getState().session.sections?.rhhab?.voir ?? rhAcces?.acces)) return <AccesRefuseRh />;
  const estRQ = session.roles.includes('rq');

  const enregistrer = async () => {
    if (!edite) return;
    setEnCours(true);
    const r = await fetchEcriture('/api/ged/ged/rh/grilles/ligne', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      // ⚠️ `actif` est envoyé EXPLICITEMENT. Le moteur lit `actif: b.actif !== false` :
      // omettre le champ vaut `true`, et corriger le libellé d'une ligne retirée
      // l'aurait donc REMISE EN SERVICE sans que rien ne le dise.
      body: JSON.stringify({ ...edite, actif: edite.actif !== false }),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setErreur(d?.error?.message || 'Modification refusée.'); return; }
    setErreur(''); setEdite(null); recharger();
  };

  /** Ouvre le panneau de retrait — et lit d'abord ce qui dépend de la ligne.
   *  Cette lecture est le seul appel réseau du geste tant qu'on n'a pas
   *  confirmé : annuler ne laisse aucune écriture derrière lui. */
  const demanderRetrait = async (ligne: Ligne, grille: Grille) => {
    setErreur('');
    setRetrait({ ligne, grille });
    if (dossiers) return;
    setLectureUsages(true);
    const r = await fetch('/api/ged/ged/rh/grilles/dossiers', { credentials: 'include' });
    const d = await r.json().catch(() => null);
    setLectureUsages(false);
    if (!r.ok || !d) { setDossiers([]); setErreur('Les dossiers de grille n’ont pas pu être lus : ce que le retrait emporte ne peut pas être établi.'); return; }
    setDossiers((d.dossiers || []) as DossierUsage[]);
  };

  const confirmerRetrait = async (): Promise<string> => {
    if (!retrait) return 'Aucune ligne visée.';
    const r = await fetchEcriture(`/api/ged/ged/rh/grilles/ligne/${retrait.ligne.id}`, {
      method: 'DELETE', credentials: 'include',
    });
    const d = await r.json().catch(() => null);
    if (!r.ok) return d?.error?.message || 'Retrait refusé.';
    setRetrait(null); setDossiers(null); recharger();
    return '';
  };

  /** Remettre en service : la preuve que le geste était bien réversible. Passe
   *  par `saveLigne` avec `actif: true` — il n'existe aucune autre route. */
  const remettre = async (l: Ligne, grilleCode: string) => {
    setEnCours(true);
    const r = await fetchEcriture('/api/ged/ged/rh/grilles/ligne', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...l, grilleCode, actif: true }),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setErreur(d?.error?.message || 'Remise en service refusée.'); return; }
    setErreur(''); recharger();
  };

  // La requalification du 07/08 : elle ne déplace AUCUNE ligne, elle range
  // quatre grilles selon leur nature réelle. Elle est rejouable, et rend le
  // compte des lignes qui changent de rattachement — c'est ce compte qu'on
  // affiche, parce que c'est la question qu'on posera.
  const requalifier = async () => {
    setEnCours(true);
    const r = await fetchEcriture('/api/ged/ged/rh/grilles/requalifier', { method: 'POST', credentials: 'include' });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setErreur(d?.error?.message || 'Requalification refusée.'); return; }
    setErreur('');
    setRequalif(d?.requalifiees?.length
      ? `${d.requalifiees.length} grille(s) requalifiée(s) — ${d.lignesRerattachees} ligne(s) changent de rattachement, aucune n’est déplacée.${d.disciplinesCreees?.length ? ` Discipline(s) créée(s) : ${d.disciplinesCreees.join(', ')}.` : ''}${d.posteCree ? ` Poste « ${d.posteCree} » créé, sa composition reste à choisir.` : ''}`
      : 'Rien à requalifier : les quatre entrées sont déjà rangées selon leur nature.');
    recharger();
  };

  if (!reg) return <Skeleton />;

  /* Les sections DÉJÀ employées, toutes grilles confondues : c'est le
     vocabulaire de fait, et il n'en existe pas d'autre — aucune table ne les
     déclare. Trié pour que la liste ne change pas d'ordre d'une ouverture à
     l'autre. */
  const sectionsConnues = [...new Set(
    reg.grilles.flatMap((g) => (g.lignes || []).map((l) => String(l.section || '').trim())),
  )].filter(Boolean).sort((a, b) => a.localeCompare(b, 'fr'));
  const parCode = new Map(reg.grilles.map((g) => [g.code, g]));
  const perimetreDe = new Map((reg.perimetres || []).map((p) => [p.code, p]));
  const groupes = entree === 'paillasse' ? reg.parPaillasse
    : entree === 'poste' ? reg.parPoste
      : (reg.parNature || []).map((n) => ({
        code: n.nature, libelle: NATURES[n.nature]?.long || n.nature, grilles: n.grilles,
      }));
  // Ce qui n'est rattaché à rien n'est pas caché : il forme son propre groupe.
  // ⚠️ Une sous-paillasse ou un bloc transverse SANS discipline n'est plus une
  // orpheline : c'est leur nature. Ne restent signalées que les paillasses sans
  // discipline — celles qui n'auront jamais de case dans la matrice.
  const orphelines = entree === 'paillasse' ? reg.sansPaillasse : entree === 'poste' ? reg.sansPoste : [];
  const rap = reg.rapprochement;

  return (
    <section>
      <TitrePage eyebrow={<>Plannings &amp; RH</>} titre="Grilles d’habilitation"
        sousTitre={<>
          Chaque compétence est une <strong>ligne modifiable</strong>, avec sa modalité de validation et son
          niveau. Les lignes de <strong>routine</strong> sont obligatoires pour tenir le poste ; les lignes
          <strong> expert</strong> s’y ajoutent sans le conditionner. <RefISO code="6.2" />
          <br />
          <span style={{ fontSize: 12.5 }}>
            Reprises de <strong>{reg.versionSource}</strong> — depuis cette reprise, ce sont ces données qui
            font foi, et c’est la plateforme qui édite la grille papier.
          </span>
        </>}
        actions={estRQ && (
          <Bouton variante="primaire" taille="sm" onClick={() => setNouvelleGrille({ code: '', libelle: '', nature: 'paillasse', discipline: '' })}>
            <I n="plus" s={13} /> Nouvelle grille
          </Bouton>
        )} />

      {!!erreur && (
        <div style={{ ...card, padding: 14, marginTop: 12, background: 'var(--red-50)', border: '1px solid #E4A0A0' }}>
          <strong style={{ fontSize: 12.5 }}>{erreur}</strong>
        </div>
      )}

      {!!requalif && (
        <div style={{ ...card, padding: 14, marginTop: 12, background: 'var(--green-50)', border: '1px solid #1F7A45' }}>
          <strong style={{ fontSize: 12.5, color: 'var(--green-600)' }}>{requalif}</strong>
        </div>
      )}

      {/* LE RAPPROCHEMENT DES TROIS LISTES — un constat, jamais une action.
          L'écran ne propose aucun bouton « aligner » : renommer une discipline
          déplacerait le filtrage des documents, des cartes de contrôle et des
          habilitations, et personne ne l'a demandé. */}
      {!!rap && (
        <div style={{ ...card, padding: 18, marginTop: 14 }}>
          <h2 style={{ fontSize: 14, margin: '0 0 6px' }}>
            Les trois listes : {rap.nbPaillasses} paillasse(s) · {rap.nbDisciplines} discipline(s) Qualité · {rap.nbFamilles} famille(s) au catalogue
          </h2>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.55 }}>
            {rap.avertissement}
          </p>
          <div style={{ display: 'grid', gap: 6, fontSize: 12.5 }}>
            <div><strong>{rap.communs.length} nom(s) commun(s)</strong> aux disciplines et au catalogue : {rap.communs.join(' · ') || '—'}</div>
            {!!rap.famillesSansDiscipline.length && (
              <div style={{ color: 'var(--amber-600)' }}>
                <strong>Au catalogue, absent des disciplines Qualité :</strong> {rap.famillesSansDiscipline.join(' · ')}
                <br /><span style={{ color: 'var(--text-muted)' }}>Des examens y sont rangés, mais aucun document ni carte de contrôle ne peut s’y filtrer.</span>
              </div>
            )}
            {!!rap.disciplinesSansFamille.length && (
              <div style={{ color: 'var(--text-muted)' }}>
                <strong>Discipline Qualité sans famille au catalogue :</strong> {rap.disciplinesSansFamille.join(' · ')}
                <br />Attendu pour les disciplines qui ne rangent pas d’examens (Qualité, Métrologie, Pré-analytique) — à regarder pour les autres.
              </div>
            )}
            {!!rap.paillassesSansDiscipline.length && (
              <div style={{ color: 'var(--amber-600)' }}>
                <strong>Paillasse sans discipline :</strong> {rap.paillassesSansDiscipline.join(' · ')}
                <br /><span style={{ color: 'var(--text-muted)' }}>Aucune décision d’habilitation prise sur elle n’entrera dans la matrice.</span>
              </div>
            )}
          </div>
          {estRQ && (
            <Bouton taille="sm" disabled={enCours} onClick={() => void requalifier()} style={{ marginTop: 12 }}>
              <I n="refresh" s={12} /> Appliquer la requalification du 07/08
            </Bouton>
          )}
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '8px 0 0', lineHeight: 1.5 }}>
            La requalification range les quatre entrées laissées sans discipline selon leur nature réelle
            et crée les disciplines que cela suppose. Elle <strong>ne déplace aucune ligne</strong>, elle est
            rejouable, et elle ne compose aucun poste à votre place.
          </p>
        </div>
      )}

      {!!reg.sansParent?.length && (
        <div style={{ ...card, padding: 16, marginTop: 12, background: 'var(--amber-50)', border: '1px solid var(--amber-500)' }}>
          <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>
            {reg.sansParent.length} sous-paillasse(s) sans paillasse parente
          </strong>
          <p style={{ fontSize: 12.5, margin: '4px 0 0', lineHeight: 1.5 }}>
            {reg.sansParent.map((c) => parCode.get(c)?.libelle || c).join(' · ')}
            <br />Leurs lignes n’entreront dans aucun dossier tant qu’aucune paillasse ne les reçoit.
          </p>
        </div>
      )}

      {/* GED-15 c — « ma décision atteint-elle la matrice ? ». Le geste est ici
          parce que c'est ici qu'on prononce, et que la matrice, elle, n'est
          jamais écrite : elle se recompose à la lecture. */}
      <ApportDesGrilles nomDe={(orgId) => nomDe(rhPersonnel.find((p) => p.orgId === orgId))} />

      <div style={{ margin: '18px 0 6px' }}>
        <Segment ariaLabel="Vue des grilles" valeur={entree} onChange={(v) => setEntree(v as typeof entree)}
          options={([['paillasse', `Par paillasse (${reg.parPaillasse.length})`], ['poste', `Par poste (${reg.parPoste.length})`], ['nature', 'Par nature'],
          /* La VUE PAR AXE (25/08/2026) — la 4e entrée, et la seule qui ne
             regroupe pas des GRILLES : elle interroge les TÂCHES elles-mêmes.
             C'est là qu'on voit qu'une tâche vit dans plusieurs vues sans être
             dupliquée nulle part. */
          ['axe', 'Par tâche']] as const).map(([valeur, libelle]) => ({ valeur, libelle }))} />
      </div>

      {entree === 'axe' && <VueParAxe />}

      {!!orphelines.length && (
        <div style={{ ...card, padding: 16, marginTop: 12, background: 'var(--amber-50)', border: '1px solid var(--amber-500)' }}>
          <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>
            {orphelines.length} grille(s) rattachée(s) à aucun{entree === 'paillasse' ? 'e paillasse' : ' poste'}
          </strong>
          <p style={{ fontSize: 12.5, margin: '4px 0 0', lineHeight: 1.5 }}>
            {orphelines.map((c) => parCode.get(c)?.libelle || c).join(' · ')}
            <br />
            {entree === 'paillasse'
              ? 'Ce sont des paillasses : elles devraient porter une discipline du module Qualité. Sans elle, aucune décision d’habilitation prise sur leur grille n’entrera dans la matrice. Le rattachement est une décision du laboratoire — il n’est pas deviné.'
              : 'Elles n’apparaîtront dans aucune fiche de poste tant que le rattachement n’est pas fait.'}
          </p>
        </div>
      )}

      <div style={{ display: 'grid', gap: 14, marginTop: 16 }}>
        {[...groupes.map((g) => ({ titre: `${g.libelle}`, codes: g.grilles })),
          { titre: entree === 'paillasse' ? 'Paillasse sans discipline' : entree === 'poste' ? 'Sans poste' : '', codes: orphelines }]
          .filter((g) => g.codes.length)
          .map((groupe) => (
            <div key={groupe.titre} style={{ ...card, padding: 18 }}>
              <h2 style={{ fontSize: 14, margin: '0 0 10px' }}>{groupe.titre}</h2>
              <div style={{ display: 'grid', gap: 10 }}>
                {groupe.codes.map((code) => {
                  const g = parCode.get(code);
                  if (!g) return null;
                  const ouvert = ouverte === g.code;
                  return (
                    <div key={g.code} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
                      <button
                        onClick={() => setOuverte(ouvert ? '' : g.code)} aria-expanded={ouvert} className="bio-btn bio-btn--contour"
                        style={{ ...styleBouton('contour', 'sm'), width: '100%', border: 0, textAlign: 'left', display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px' }}
                      >
                        {/* ⚠️ `chevron-bas`, pas `chevron-down` : `I` dessine un SVG VIDE
                            sur un nom inconnu, sans erreur — le chevron manquait ici. */}
                        <I n={ouvert ? 'chevron-bas' : 'chevron-right'} s={14} />
                        <strong style={{ fontSize: 13, opacity: g.actif ? 1 : 0.6 }}>{g.libelle}</strong>
                        <BadgeNature n={g.nature} />
                        {/* ⚠️ UNE GRILLE RETIRÉE DU SERVICE NE SE VOYAIT PAS. Le
                            moteur refuse d'ouvrir un dossier dessus, mais l'écran
                            la rendait comme les autres : on cliquait, et le refus
                            arrivait sans qu'on comprenne pourquoi. C'est aussi ce
                            qui tient la grille « biologiste », proposée et non
                            encore relue, à l'écart des habilitations. */}
                        {!g.actif && (
                          <span title="Aucun dossier d’habilitation ne peut être ouvert sur cette grille tant qu’elle est hors service."
                            style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '1px 7px', background: 'var(--gray-100)', color: 'var(--text-muted)' }}>hors service</span>
                        )}
                        {g.origine === 'proposition' && (
                          <span title="Rédigée d’après la norme, relue par personne à ce jour. À relire ligne à ligne, puis à activer."
                            style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '1px 7px', background: 'var(--amber-50)', color: 'var(--amber-600)' }}>proposition</span>
                        )}
                        {g.sousTraitance && (
                          <span title="Activité aujourd’hui confiée à un laboratoire extérieur. Elle reste une paillasse : le registre des sous-traitants dit chez qui."
                            style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '1px 7px', background: 'var(--amber-50)', color: 'var(--amber-600)' }}>sous-traitée</span>
                        )}
                        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                          {g.nbRoutine} routine · {g.nbExpert} expert · v{g.version}
                          {g.cycle === 2 ? ' · cycle 2' : ''}
                        </span>
                      </button>
                      {ouvert && (
                        <div style={{ padding: '0 12px 12px' }}>
                          {/* Lot 3 (11/08/2026) — l'ajout d'une ligne manquait :
                              la grille se modifiait mais ne s'enrichissait pas. */}
                          {estRQ && (
                            <div style={{ margin: '2px 0 8px' }}>
                              <Bouton variante="primaire" taille="sm" onClick={() => setEdite({ grilleCode: g.code, niveau: 'routine' })}>
                                <I n="plus" s={12} /> Ajouter une ligne de compétence
                              </Bouton>
                            </div>
                          )}
                          {!!g.chapo && <p style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic', margin: '0 0 8px' }}>{g.chapo}</p>}
                          {/* Lot 3 du parcours (18/10/2026) — LES DOCUMENTS DE RÉFÉRENCE :
                              ce qu'il faut avoir lu, avec accusé, pour tenir cette paillasse.
                              Le parcours du formé les montre comme lectures obligatoires et
                              la RQ les lui diffuse d'un geste. Une route à part : on n'édite
                              que ce champ, jamais toute la grille. */}
                          <div style={{ margin: '0 0 10px', padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px dashed var(--border-default)' }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 }}>Documents de référence à lire pendant la formation</div>
                            <SelecteurPastilles mono modifiable={estRQ} unite="document"
                              ariaLabel={`Documents de référence de la grille ${g.libelle}`}
                              placeholder="Ajouter un document par son code ou son titre…"
                              vide="Aucun document désigné : le parcours ne demandera aucune lecture pour cette grille."
                              options={docs.map((d) => ({ documentId: d.code, code: d.code, nom: d.titre }))}
                              choisis={g.documents || []}
                              onChange={(codes) => {
                                void (async () => {
                                  const r = await fetchEcriture(`/api/ged/ged/rh/grilles/${encodeURIComponent(g.code)}/documents`, {
                                    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ documents: codes }),
                                  });
                                  if (!r.ok) { const j = await r.json().catch(() => ({})); setErreur(j?.error?.message || 'Modification refusée.'); return; }
                                  setErreur(''); recharger();
                                })();
                              }} />
                          </div>
                          {/* Ce que la grille REÇOIT, et ce qu'elle DONNE — dit en
                              toutes lettres, parce que « sans duplication » est
                              invisible autrement : les lignes n'apparaissent pas
                              deux fois, et il faut donc dire qu'elles comptent. */}
                          {g.nature === 'paillasse' && !!perimetreDe.get(g.code)?.jointes.length && (
                            <p style={{ fontSize: 12.5, margin: '0 0 8px', lineHeight: 1.5, color: 'var(--blue-700)' }}>
                              Reçoit <strong>{perimetreDe.get(g.code)!.nbLignesJointes} ligne(s)</strong> de{' '}
                              {perimetreDe.get(g.code)!.jointes.map((c) => parCode.get(c)?.libelle || c).join(' · ')}.
                              <span style={{ color: 'var(--text-muted)' }}> Elles ne sont pas recopiées ici : elles comptent dans le dossier
                                et s’impriment sur la pochette, mais restent écrites une seule fois, dans leur propre grille.</span>
                            </p>
                          )}
                          {g.nature === 'sous-paillasse' && (
                            <p style={{ fontSize: 12.5, margin: '0 0 8px', lineHeight: 1.5, color: 'var(--blue-700)' }}>
                              Rattachée à <strong>{g.paillasses.map((c) => parCode.get(c)?.libelle || c).join(' · ') || 'aucune paillasse'}</strong>.
                              <span style={{ color: 'var(--text-muted)' }}> Ses lignes apparaissent dans le dossier de chacune, sans y être dupliquées.</span>
                            </p>
                          )}
                          {g.nature === 'transverse' && (
                            <p style={{ fontSize: 12.5, margin: '0 0 8px', lineHeight: 1.5, color: 'var(--violet-600)' }}>
                              Présent dans <strong>tout dossier de grille</strong>, quelle que soit la paillasse, sans exception —
                              <span style={{ color: 'var(--text-muted)' }}> et sans que ses lignes soient recopiées dans chacune.</span>
                            </p>
                          )}
                          {g.nature === 'poste' && (
                            <p style={{ fontSize: 12.5, margin: '0 0 8px', lineHeight: 1.5, color: 'var(--amber-600)' }}>
                              Lignes propres au poste <strong>{g.posteCode || '(non désigné)'}</strong> : elles n’entrent dans aucun dossier de paillasse.
                              <span style={{ color: 'var(--text-muted)' }}> Ce que le poste exige en plus se choisit dans l’onglet « Composition des postes ».</span>
                            </p>
                          )}
                          {/* L5 (10/10/2026) : le tableau du socle, niveau standard — la
                              section devient une colonne (triable, filtrable en liste) au
                              lieu d'une rangée d'intertitre ; les gestes RQ passent en colonne
                              d'actions ; une ligne retirée reste lisible, barrée et pâlie. */}
                          <Tableau
                            id={`rh-grille-${g.code}`}
                            niveau="standard"
                            lignes={g.lignes}
                            cleLigne={(l) => l.code}
                            vide={{ titre: 'Aucune ligne', indice: 'Cette grille ne porte encore aucune compétence.' }}
                            ligneProps={(l) => ({ style: { opacity: l.actif ? 1 : 0.55 } })}
                            colonnes={[
                              { cle: 'section', libelle: 'Section', type: 'liste', valeur: (l) => l.section || '', style: { color: 'var(--color-primary)', fontWeight: 700 }, rendu: (l) => l.section || '—' },
                              {
                                cle: 'libelle', libelle: 'Compétence', valeur: (l) => l.libelle, obligatoire: true, style: { whiteSpace: 'normal', lineHeight: 1.45 },
                                rendu: (l) => (
                                  <>
                                    <span style={{ textDecoration: l.actif ? 'none' : 'line-through' }}>{l.libelle}</span>
                                    {!l.actif && (
                                      <span title="Ligne retirée : elle ne compte plus dans l’avancement ni dans le prononcé, et ne s’imprime plus. Les résultats déjà évalués dessus restent lisibles dans les dossiers."
                                        style={{ marginInlineStart: 8, fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '1px 7px', background: 'var(--amber-50)', color: 'var(--amber-600)', cursor: 'help' }}>retirée</span>
                                    )}
                                  </>
                                ),
                              },
                              { cle: 'methode', libelle: 'Méthode', type: 'liste', valeur: (l) => l.methode, style: { width: 120 }, rendu: (l) => <Methode code={l.methode} /> },
                              { cle: 'niveau', libelle: 'Niveau', type: 'liste', valeur: (l) => l.niveau, style: { width: 78 }, rendu: (l) => <Niveau n={l.niveau} /> },
                            ]}
                            actions={estRQ ? (l) => (l.actif ? (
                              <>
                                <Bouton taille="sm" onClick={() => setEdite({ ...l, grilleCode: g.code })}>
                                  <I n="pen" s={12} /> Modifier
                                </Bouton>
                                <Bouton taille="sm" onClick={() => void demanderRetrait(l, g)}>
                                  <I n="trash" s={12} /> Retirer
                                </Bouton>
                              </>
                            ) : (
                              <Bouton taille="sm" disabled={enCours} onClick={() => void remettre(l, g.code)}>
                                <I n="refresh" s={12} /> Remettre en service
                              </Bouton>
                            )) : undefined}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
      </div>

      {!!retrait && (
        <div style={{ marginTop: 18 }}>
          {lectureUsages ? (
            <div style={{ ...card, padding: 16, fontSize: 12.5, color: 'var(--text-muted)' }}>
              Lecture des dossiers de grille — ce que le retrait emporte n’est pas encore établi…
            </div>
          ) : (
            <SuppressionRaisonnee
              nature="retrait"
              titre="Retirer une ligne de compétence"
              objet={<>
                <strong>{retrait.ligne.libelle}</strong>
                {' — '}grille « {retrait.grille.libelle} », niveau {retrait.ligne.niveau}
                {retrait.ligne.section ? `, section « ${retrait.ligne.section} »` : ''}.
              </>}
              consequences={consequencesLigne(retrait.ligne, retrait.grille, dossiers || [])}
              libelleAction="Retirer la ligne"
              onConfirmer={confirmerRetrait}
              onAnnuler={() => setRetrait(null)}
              note={<>
                Le moteur <strong>ne supprime pas</strong> la ligne : il la désactive. C’est délibéré —
                effacer la ligne rendrait illisibles les résultats déjà évalués dessus (« acquis sur
                quoi ? »). Aucune route ne permet de la supprimer réellement, et il n’est pas prévu
                d’en ouvrir une.
              </>}
            />
          )}
        </div>
      )}

      {/* ⚠️ UNE FENÊTRE, ET PLUS UN PANNEAU EN BAS DE PAGE (12/09/2026).
          « Le bouton nouvelle grille ne fonctionne pas. Je veux un pop up. » —
          RQ, 11/09. Le bouton fonctionnait : il posait bien `nouvelleGrille`, et
          ce bloc s'affichait — mais APRÈS la liste complète des grilles, soit
          plusieurs hauteurs de fenêtre plus bas, et rien n'y défilait. Un geste
          sans effet VISIBLE est, pour qui l'utilise, un geste sans effet.
          La `Modale` du socle apporte en plus ce qu'un panneau n'avait pas : le
          focus qui entre et reste dedans, la page de dessous qui ne défile plus,
          et l'alerte si l'on sort d'une saisie commencée. */}
      {!!nouvelleGrille && (
        <Modale titre="Nouvelle grille d’habilitation" largeur={620}
          onClose={() => setNouvelleGrille(null)}
          pied={<>
            <Bouton taille="sm" onClick={() => setNouvelleGrille(null)}>Annuler</Bouton>
            <Bouton variante="primaire" taille="sm" disabled={enCours} onClick={() => {
              void (async () => {
                setEnCours(true);
                try {
                  const r = await fetchEcriture('/api/ged/ged/rh/grilles', {
                    method: 'POST', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      code: nouvelleGrille.code, libelle: nouvelleGrille.libelle,
                      nature: nouvelleGrille.nature,
                      discipline: nouvelleGrille.discipline || null,
                    }),
                  });
                  if (!r.ok) {
                    const d = await r.json().catch(() => ({}));
                    setErreur(d?.error?.message || 'Création refusée.');
                    return;
                  }
                  setErreur(''); setNouvelleGrille(null); recharger();
                } finally { setEnCours(false); }
              })();
            }}>
              {enCours ? 'Création…' : 'Créer la grille'}
            </Bouton>
          </>}>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
            <label style={{ fontSize: 12.5 }}>Code *
              <input style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} value={nouvelleGrille.code}
                placeholder="GRILLE-J"
                onChange={(e) => setNouvelleGrille({ ...nouvelleGrille, code: e.target.value.toUpperCase() })} />
            </label>
            <label style={{ fontSize: 12.5 }}>Libellé *
              <input style={inputStyle} value={nouvelleGrille.libelle}
                onChange={(e) => setNouvelleGrille({ ...nouvelleGrille, libelle: e.target.value })} />
            </label>
            <label style={{ fontSize: 12.5 }}>Nature
              <select style={inputStyle} value={nouvelleGrille.nature}
                onChange={(e) => setNouvelleGrille({ ...nouvelleGrille, nature: e.target.value })}>
                <option value="paillasse">Paillasse (discipline)</option>
                <option value="sous-paillasse">Sous-paillasse</option>
                <option value="poste">Poste</option>
                <option value="transverse">Transverse</option>
              </select>
            </label>
            <label style={{ fontSize: 12.5 }}>Discipline (code du paramétrage Qualité, pour une paillasse)
              <input style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} value={nouvelleGrille.discipline}
                placeholder="BIOCH" onChange={(e) => setNouvelleGrille({ ...nouvelleGrille, discipline: e.target.value.toUpperCase() })} />
            </label>
          </div>
        </Modale>
      )}

      {/* ⚠️ EN FENÊTRE AUSSI (12/09/2026), et pour la même raison : « déjà quand
          je veux modifier une compétence, il faut qu'elle apparaisse comme pop
          up » — RQ, 11/09. Ce panneau se rendait sous les 399 lignes de
          compétence de la page ; cliquer « modifier » ne montrait rien.
          ⚠️ Plus large que la précédente : elle porte la compétence, sa
          modalité, son niveau ET ses rattachements. La `Modale` fait défiler son
          CONTENU et non la page — c'est ce qui la rend tenable sans découper le
          formulaire en deux. */}
      {!!edite && (
        <Modale largeur={680}
          titre={edite.id || edite.code ? 'Modifier une ligne de compétence' : 'Ajouter une ligne de compétence'}
          onClose={() => setEdite(null)}
          pied={<>
            <Bouton taille="sm" onClick={() => setEdite(null)}>Annuler</Bouton>
            <Bouton variante="primaire" disabled={enCours} onClick={() => void enregistrer()}>Enregistrer</Bouton>
          </>}>
          <div style={{ display: 'grid', gap: 10 }}>
            <label style={{ fontSize: 12.5 }}>Compétence
              <textarea style={{ ...inputStyle, minHeight: 64 }} value={edite.libelle || ''}
                onChange={(e) => setEdite({ ...edite, libelle: e.target.value })} />
            </label>
            {/* ⚠️ 012 — « La section doit être une liste déroulante également ;
                prévoir de pouvoir rajouter une nouvelle section » (RQ, 11/09).
                Champ libre, le registre a produit 78 sections distinctes pour
                399 lignes : mesuré en base, et c'est exactement le mécanisme
                des 54 graphies de salle. La liste propose ce qui existe DÉJÀ ;
                « Nouvelle section… » ouvre la saisie, explicitement.
                ⚠️ Ce n'est PAS une échappatoire « autre… » : celle-là se remplit
                dès le premier oubli et l'on retrouve les graphies. Ici, créer
                est un geste séparé, qu'on choisit. */}
            <ChampSection valeur={edite.section || ''} sections={sectionsConnues}
              onChange={(v) => setEdite({ ...edite, section: v })} />
            {/* ⚠️ 011 — l'ordre des codes fait partie de la valeur : c'est
                `ecrireModalites` qui le fixe, et lui seul. Voir
                `lib/modalites-validation.ts` pour les six lignes mal rangées
                que ce champ libre avait produites. */}
            <label style={{ fontSize: 12.5 }}>Modalité de validation (combinables)
              <SelecteurPastilles mono modifiable unite="modalité"
                ariaLabel="Modalités de validation de cette compétence"
                placeholder="Choisir une modalité…"
                vide="Aucune — la grille d’origine ne dit pas comment cette ligne se valide."
                options={MODALITES.map((m) => ({ documentId: m.code, code: m.code, nom: m.libelle }))}
                choisis={lireModalites(edite.methode)}
                onChange={(codes) => setEdite({ ...edite, methode: ecrireModalites(codes) })} />
            </label>
            {/* Lot 4 du parcours (18/10/2026) : les mises en situation (QCM, corrigés
                par le serveur) qui évaluent cette compétence. Le formé les voit « à
                passer » depuis son parcours ; un questionnaire réussi apparaît sur la
                ligne pour le tuteur, qui confirme. Un score ne prononce rien. */}
            <label style={{ fontSize: 12.5 }}>Mises en situation qui évaluent cette compétence
              <SelecteurPastilles mono={false} modifiable unite="mise en situation"
                ariaLabel="Mises en situation reliées à cette compétence"
                placeholder="Relier une mise en situation par son titre ou sa discipline…"
                vide="Aucune — la ligne s’évalue par observation ou vérification documentaire, sans questionnaire."
                options={situations.map((x) => ({ documentId: x.documentId, code: `${x.titre}${x.statut !== 'valide' ? ' (brouillon)' : ''}`, nom: x.discipline }))}
                choisis={edite.situations || []}
                onChange={(ids) => setEdite({ ...edite, situations: ids })} />
            </label>
            <label style={{ fontSize: 12.5 }}>Niveau exigé par défaut
              <select style={inputStyle} value={edite.niveau || 'routine'}
                onChange={(e) => setEdite({ ...edite, niveau: e.target.value as NiveauTache })}>
                <option value="routine">Routine — obligatoire pour tenir le poste</option>
                <option value="avance">Avancé — au-delà de la routine, sans être spécialiste</option>
                <option value="expert">Expert — maintenance lourde, dépannage, arbitrage</option>
              </select>
            </label>

            {/* ── LES RATTACHEMENTS DE LA TÂCHE (25/08/2026) ──────────────────
                Avant, ils venaient de la GRILLE : ranger une tâche, c'était
                choisir une grille, et elle n'apparaissait nulle part ailleurs.
                Déclarés ici, ils font apparaître la tâche dans TOUTES les vues
                qui la concernent — sans qu'elle soit dupliquée nulle part. */}
            <fieldset style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '12px 14px', margin: 0, display: 'grid', gap: 10 }}>
              <legend style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', padding: '0 6px' }}>
                À quoi cette tâche se rapporte
              </legend>
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                Laissé <strong>vide</strong>, chaque champ hérite de la grille — c’est le cas des
                tâches reprises, et rien ne les oblige à être ressaisies. Renseigné, il
                <strong> remplace</strong> l’héritage : c’est ce qui permet de sortir une tâche de
                l’axe de sa grille.
              </p>

              <label style={{ fontSize: 12.5 }}>Postes qui l’exigent, et à quel niveau
                <ChampPostes valeur={edite.postes || []} onChange={(v) => setEdite({ ...edite, postes: v })}
                  niveauParDefaut={(edite.niveau as NiveauTache) || 'routine'} />
              </label>

              {/* ⚠️ 011 — un code tapé à la main ne se rattache à rien : « EQ12 »
                  mal frappé produit une tâche liée à un appareil qui n'existe
                  pas, et l'écran de l'équipement ne dira jamais qu'il lui manque
                  quelqu'un. La liste ne propose que le parc réel. */}
              <label style={{ fontSize: 12.5 }}>Équipement sur lequel elle s’exécute
                <Selecteur value={edite.equipementCode || ''} style={inputStyle}
                  onChange={(v) => setEdite({ ...edite, equipementCode: v })}>
                  <option value="">— aucun —</option>
                  {(equips || []).filter((e) => e.code && e.actif !== false)
                    .sort((a, b) => String(a.code).localeCompare(String(b.code)))
                    .map((e) => <option key={e.documentId} value={String(e.code)}>{e.code} — {e.nom}</option>)}
                  {/* Une valeur héritée qui ne figure plus au parc doit rester
                      sélectionnée, sinon rouvrir la ligne la remplacerait en
                      silence par la première de la liste. */}
                  {!!edite.equipementCode && !(equips || []).some((e) => e.code === edite.equipementCode) && (
                    <option value={edite.equipementCode}>{edite.equipementCode} — hors parc</option>
                  )}
                </Selecteur>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  C’est ce lien — et lui seul — qui fait qu’un appareil sait dire qui le maîtrise.
                  On ne s’habilite jamais « à un automate » : on est habilité à un poste.
                </span>
              </label>

              {/* ⚠️ 011 — « pour les disciplines, sélection multiple. Processus
                  pareil » (RQ, 11/09). C'étaient deux champs de texte à
                  virgules : il fallait connaître les codes par cœur, et rien ne
                  refusait un code inexistant. Les deux axes restent SÉPARÉS —
                  une discipline est une famille du catalogue, un processus une
                  case de la cartographie ; les fondre a déjà été refusé. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ fontSize: 12.5 }}>Disciplines
                  <SelecteurPastilles mono modifiable unite="discipline"
                    ariaLabel="Disciplines auxquelles cette tâche se rapporte"
                    placeholder="Choisir une discipline…"
                    vide="Aucune — la tâche hérite de l’axe de sa grille."
                    options={(settings?.disciplines || []).filter((d) => d.active)
                      .map((d) => ({ documentId: String(d.code || d.name), code: String(d.code || d.name), nom: d.name || String(d.code) }))}
                    choisis={edite.disciplines || []}
                    onChange={(v) => setEdite({ ...edite, disciplines: v })} />
                </label>
                <label style={{ fontSize: 12.5 }}>Processus
                  <SelecteurPastilles mono={false} modifiable unite="processus"
                    ariaLabel="Processus auxquels cette tâche se rapporte"
                    placeholder="Choisir un processus…"
                    vide="Aucun — la tâche hérite de l’axe de sa grille."
                    options={((settings?.processus || []) as { id: string; name: string }[])
                      .map((x) => ({ documentId: x.id, code: x.name || x.id, nom: x.name || x.id }))}
                    choisis={edite.processus || []}
                    onChange={(v) => setEdite({ ...edite, processus: v })} />
                </label>
              </div>
            </fieldset>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
              Enregistrer fait monter la version de la grille : c’est ce numéro que porte le cartouche
              de la grille imprimée. Les dossiers déjà ouverts gardent la version sur laquelle ils ont
              été évalués.
            </p>
          </div>
        </Modale>
      )}
    </section>
  );
}
